/**
 * Hide guest-only Login / Sign up controls in injected Webflow HTML when the
 * viewer is authenticated. Matches by href path and by link/button text.
 */

const GUEST_AUTH_PATHS = [
  '/login',
  '/signup',
  '/sign-up',
  '/sign_up',
  '/register',
  '/log-in',
  '/sign-in',
  '/memberships',
  '/join',
] as const;

const GUEST_AUTH_LABEL_RE =
  /^(log\s*in|login|sign\s*up|signup|sign\s*in|signin|register|join(\s+now)?)\b/i;

const NAV_MARKER_ID = 'data-tradewing-nav';

const normalizePathname = (pathname: string): string => {
  const trimmed = pathname.replace(/\/+$/, '');
  return (trimmed || '/').toLowerCase();
};

const isGuestAuthHref = (href: string): boolean => {
  const trimmed = href.trim();
  if (
    !trimmed ||
    trimmed.startsWith('#') ||
    trimmed.startsWith('mailto:') ||
    trimmed.startsWith('tel:') ||
    trimmed.startsWith('javascript:')
  ) {
    return false;
  }

  try {
    const url = new URL(trimmed, 'https://tradewing.local');
    const path = normalizePathname(url.pathname);
    return GUEST_AUTH_PATHS.some(
      guestPath => path === guestPath || path.startsWith(`${guestPath}/`)
    );
  } catch {
    return false;
  }
};

const isGuestAuthLabel = (text: string): boolean => {
  const normalized = text.replace(/\s+/g, ' ').trim();
  if (!normalized || normalized.length > 24) return false;
  return GUEST_AUTH_LABEL_RE.test(normalized);
};

const isGuestAuthControl = (el: HTMLElement): boolean => {
  if (el instanceof HTMLAnchorElement) {
    const href = el.getAttribute('href') || '';
    if (isGuestAuthHref(href)) return true;
  }

  if (isGuestAuthLabel(el.textContent || '')) return true;

  return false;
};

const resolveHideTarget = (
  el: HTMLElement,
  root: HTMLElement
): HTMLElement => {
  const parent = el.parentElement;
  if (parent && parent !== root && parent.children.length === 1) {
    return parent;
  }
  return el;
};

const resolveNavScope = (root: HTMLElement): HTMLElement => {
  if (root.id === NAV_MARKER_ID) return root;
  const marked = root.querySelector<HTMLElement>(`#${NAV_MARKER_ID}`);
  return marked || root;
};

/**
 * True when Webflow nav HTML already includes Login / Sign up / Join controls.
 * Used to avoid showing duplicate Tradewing guest buttons beside Webflow nav.
 */
export const webflowNavHtmlHasGuestAuthControls = (
  html: string | null | undefined
): boolean => {
  if (!html) return false;

  for (const match of html.matchAll(
    /<a\b[^>]*\bhref\s*=\s*(["'])(.*?)\1[^>]*>/gi
  )) {
    if (isGuestAuthHref(match[2] || '')) {
      return true;
    }
  }

  for (const match of html.matchAll(
    /<(?:a|button)\b[^>]*>([\s\S]*?)<\/(?:a|button)>/gi
  )) {
    const text = (match[1] || '').replace(/<[^>]+>/g, ' ');
    if (isGuestAuthLabel(text)) {
      return true;
    }
  }

  return false;
};

/**
 * Hide (or restore) Login/Sign up anchors/buttons inside a Webflow island.
 * Prefers scoping to `#data-tradewing-nav` when present.
 */
export const syncGuestAuthLinkVisibility = (
  root: HTMLElement,
  hide: boolean
): void => {
  const scope = resolveNavScope(root);
  const candidates = scope.querySelectorAll(
    'a[href], button, [role="button"]'
  );

  candidates.forEach(node => {
    if (!(node instanceof HTMLElement)) return;
    if (!isGuestAuthControl(node)) return;

    const target = resolveHideTarget(node, scope);

    if (hide) {
      if (target.dataset.twAuthHidden === '1') return;
      target.dataset.twAuthHidden = '1';
      target.dataset.twAuthPrevDisplay = target.style.display;
      target.style.display = 'none';
      return;
    }

    if (target.dataset.twAuthHidden !== '1') return;
    target.style.display = target.dataset.twAuthPrevDisplay || '';
    delete target.dataset.twAuthHidden;
    delete target.dataset.twAuthPrevDisplay;
  });
};
